import * as signalR from '@microsoft/signalr';

const MUTATION_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const topicHandlers = new Map();
const topicSubscriptions = new Map();
const LEASE_METHODS = new Set(['AcquireLease', 'RenewLease', 'ReleaseLease']);
const pendingMutations = new Map();
const completedMutations = new Map();
const failedMutations = new Set();
const queuedOwnEvents = new Map();
const seenEvents = new Map();
let connection;
let startPromise;
let presenceTimer;

function createClientInstanceId() {
    const values = new Uint8Array(18);
    window.crypto.getRandomValues(values);
    return Array.from(values, value => value.toString(16).padStart(2, '0')).join('');
}

const clientInstanceId = createClientInstanceId();

function categoryFromUrl(input) {
    try {
        const url = new URL(input, window.location.href);
        if (url.origin !== window.location.origin) return null;
        let path = url.pathname.toLowerCase();
        const configuredPrefix = document.documentElement.dataset.urlPrefix;
        if (configuredPrefix && configuredPrefix !== '/') {
            const prefix = new URL(configuredPrefix, window.location.origin).pathname
                .replace(/\/$/, '')
                .toLowerCase();
            if (path === prefix) path = '/';
            else if (path.startsWith(`${prefix}/`)) path = path.slice(prefix.length);
        }
        path = path.replace(/^\/api\//, '').replace(/^\//, '');
        if (path.startsWith('people')) return 'people';
        if (path.startsWith('cases') || path.startsWith('case-management') || path.startsWith('casemanagement')) return 'cases';
        if (path.startsWith('organizations')) return 'organizations';
        if (path.startsWith('events')) return 'events';
        if (path.startsWith('meetings') || path.startsWith('meeting')) return 'meetings';
        if (path.startsWith('organizing')) return 'organizing';
        if (path.startsWith('finance') || path.startsWith('expenses') || path.startsWith('payments')) return 'finance';
        if (path.startsWith('strike')) return 'strike';
        if (path.startsWith('communications') || path.startsWith('email')) return 'communications';
        if (path.startsWith('users')) return 'users';
        if (path.startsWith('settings') || path.startsWith('datatables')) return 'settings';
        if (path.startsWith('dashboard')) return 'dashboard';
    } catch {
        return null;
    }
    return null;
}

function mutationTopics(input) {
    const category = categoryFromUrl(input);
    if (!category) return [];
    const topics = new Set([`table:${category}`]);
    const recordCategory = { people: 'person', cases: 'case', organizations: 'organization' }[category];
    if (!recordCategory) return [...topics];

    try {
        const segments = new URL(input, window.location.href).pathname
            .split('/')
            .filter(Boolean)
            .map(value => value.toLowerCase());
        for (const topic of topicHandlers.keys()) {
            const [type, topicCategory, resourceId] = topic.split(':');
            if (type === 'record' && topicCategory === recordCategory && segments.includes(resourceId)) {
                topics.add(topic);
            }
        }
    } catch {
        // The category topic remains sufficient for a malformed URL to retain normal HTTP behavior.
    }
    return [...topics];
}

function beginMutation(topic) {
    if (!topic) return;
    pendingMutations.set(topic, (pendingMutations.get(topic) || 0) + 1);
}

function endMutation(topic, succeeded) {
    if (!topic) return;
    if (!succeeded) failedMutations.add(topic);
    const remaining = Math.max(0, (pendingMutations.get(topic) || 1) - 1);
    if (remaining) {
        pendingMutations.set(topic, remaining);
        return;
    }
    pendingMutations.delete(topic);
    const allSucceeded = succeeded && !failedMutations.has(topic);
    failedMutations.delete(topic);
    const queued = queuedOwnEvents.get(topic);
    queuedOwnEvents.delete(topic);
    if (allSucceeded) completedMutations.set(topic, performance.now() + 5000);
    else if (queued) emit(topic, 'resource-changed', queued);
}

function isOwnCompletedMutation(message) {
    if (!message || message.clientInstanceId !== clientInstanceId) return false;
    const expiry = completedMutations.get(message.topic) || 0;
    if (expiry > performance.now()) return true;
    completedMutations.delete(message.topic);
    return false;
}

function rememberEvent(message) {
    if (!message?.eventId) return true;
    const now = performance.now();
    for (const [id, expiry] of seenEvents) {
        if (expiry <= now) seenEvents.delete(id);
    }
    if (seenEvents.has(message.eventId)) return false;
    seenEvents.set(message.eventId, now + 60000);
    return true;
}

function handlersFor(topic) {
    if (!topicHandlers.has(topic)) topicHandlers.set(topic, new Set());
    return topicHandlers.get(topic);
}

function emit(topic, type, detail) {
    for (const handler of topicHandlers.get(topic) || []) {
        try { handler(type, detail); } catch (error) { console.warn('Real-time surface handler failed.', error); }
    }
    document.dispatchEvent(new CustomEvent(`unionstack:realtime-${type}`, { detail }));
}

function retryDelay(context) {
    const delays = [0, 2000, 10000, 30000, 60000];
    const base = delays[Math.min(context.previousRetryCount, delays.length - 1)];
    return base + Math.floor(Math.random() * Math.max(250, base * 0.2));
}

function buildConnection() {
    const prefix = document.documentElement.dataset.urlPrefix?.replace(/\/$/, '') || '';
    const instance = new signalR.HubConnectionBuilder()
        .withUrl(`${prefix}/hubs/updates`)
        .withAutomaticReconnect({ nextRetryDelayInMilliseconds: retryDelay })
        .configureLogging(signalR.LogLevel.Warning)
        .build();

    instance.on('ResourceChanged', message => {
        if (!rememberEvent(message)) return;
        if (message?.clientInstanceId === clientInstanceId && (pendingMutations.get(message.topic) || 0) > 0) {
            queuedOwnEvents.set(message.topic, message);
            return;
        }
        if (isOwnCompletedMutation(message)) return;
        emit(message.topic, 'resource-changed', message);
    });
    instance.on('OperationProgress', message => emit(`operation:${message.operationId}`, 'operation-progress', message));
    instance.on('PresenceChanged', message => emit(message.topic, 'presence-changed', message));
    instance.on('LeaseChanged', message => emit(message.topic, 'lease-changed', message));
    instance.on('SessionInvalidated', message => {
        document.dispatchEvent(new CustomEvent('unionstack:session-invalidated', { detail: message }));
        const returnUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        window.location.replace(`/session/ended?returnUrl=${encodeURIComponent(returnUrl)}`);
    });
    instance.onreconnecting(() => {
        topicSubscriptions.clear();
        document.documentElement.dataset.realtimeState = 'reconnecting';
        document.dispatchEvent(new CustomEvent('unionstack:realtime-reconnecting'));
    });
    instance.onreconnected(async () => {
        document.documentElement.dataset.realtimeState = 'connected';
        await subscribeAll();
        for (const topic of topicHandlers.keys()) emit(topic, 'reconnected', { topic });
    });
    instance.onclose(() => {
        topicSubscriptions.clear();
        document.documentElement.dataset.realtimeState = 'disconnected';
        document.dispatchEvent(new CustomEvent('unionstack:realtime-disconnected'));
    });
    return instance;
}

async function start() {
    if (startPromise) return startPromise;
    if (connection?.state === signalR.HubConnectionState.Connected) return connection;
    connection ||= buildConnection();
    // Automatic reconnect owns the connection until it completes. A second start
    // during that transition fails and can create a repeating manual retry loop.
    if (connection.state !== signalR.HubConnectionState.Disconnected) {
        throw globalThis.UnionStackI18n.error("Common.ClientTheRealTimeConnectionIsNotReadye8014e");
    }
    startPromise = (async () => {
        try {
            document.documentElement.dataset.realtimeState = 'connecting';
            await connection.start();
            document.documentElement.dataset.realtimeState = 'connected';
            await subscribeAll();
            window.clearInterval(presenceTimer);
            presenceTimer = window.setInterval(() => {
                if (connection.state === signalR.HubConnectionState.Connected) {
                    void connection.invoke('HeartbeatPresence').catch(() => {});
                }
            }, 30000);
            return connection;
        } catch (error) {
            document.documentElement.dataset.realtimeState = 'disconnected';
            window.setTimeout(() => { startPromise = null; if (topicHandlers.size) void start().catch(() => {}); }, 5000 + Math.random() * 2000);
            throw error;
        } finally {
            startPromise = null;
        }
    })();
    return startPromise;
}

async function subscribeAll() {
    if (connection?.state !== signalR.HubConnectionState.Connected) return;
    for (const topic of topicHandlers.keys()) {
        await ensureSubscribed(topic);
    }
}

function ensureSubscribed(topic) {
    if (!topicHandlers.has(topic) || connection?.state !== signalR.HubConnectionState.Connected) {
        return Promise.resolve(false);
    }
    if (topicSubscriptions.has(topic)) return topicSubscriptions.get(topic);
    const subscription = connection.invoke('Subscribe', { topic })
        .then(accepted => {
            if (!accepted) emit(topic, 'subscription-rejected', { topic });
            return Boolean(accepted && topicHandlers.has(topic) && topicSubscriptions.get(topic) === subscription);
        })
        .catch(() => {
            if (topicSubscriptions.get(topic) === subscription) topicSubscriptions.delete(topic);
            emit(topic, 'subscription-rejected', { topic });
            return false;
        });
    topicSubscriptions.set(topic, subscription);
    return subscription;
}

function subscribe(topic, handler) {
    if (!topic || typeof handler !== 'function') return () => {};
    const handlers = handlersFor(topic);
    const wasEmpty = handlers.size === 0;
    handlers.add(handler);
    if (wasEmpty) {
        if (connection?.state === signalR.HubConnectionState.Connected) {
            void ensureSubscribed(topic);
        } else {
            void start().catch(() => {});
        }
    }
    return () => {
        handlers.delete(handler);
        if (handlers.size) return;
        topicHandlers.delete(topic);
        topicSubscriptions.delete(topic);
        if (connection?.state === signalR.HubConnectionState.Connected) {
            void connection.invoke('Unsubscribe', { topic }).catch(() => {});
        }
    };
}

async function invoke(method, ...args) {
    const instance = await start();
    if (LEASE_METHODS.has(method) && !await ensureSubscribed(args[0]?.topic)) {
        throw globalThis.UnionStackI18n.error("Common.ClientTheEditingLeaseSubscriptionIsUnavailable8d5a10");
    }
    return instance.invoke(method, ...args);
}

function installFetchMutationHeaders() {
    const originalFetch = window.fetch.bind(window);
    window.fetch = async (input, init = {}) => {
        const request = input instanceof Request ? input : null;
        const url = request?.url || input;
        const method = String(init.method || request?.method || 'GET').toUpperCase();
        const topics = MUTATION_METHODS.has(method) ? mutationTopics(url) : [];
        if (!topics.length) return originalFetch(input, init);

        const headers = new Headers(request?.headers || undefined);
        new Headers(init.headers || undefined).forEach((value, name) => headers.set(name, value));
        headers.set('X-UnionStack-Client-Instance', clientInstanceId);
        topics.forEach(beginMutation);
        try {
            const response = await originalFetch(input, { ...init, headers });
            topics.forEach(topic => endMutation(topic, response.ok));
            return response;
        } catch (error) {
            topics.forEach(topic => endMutation(topic, false));
            throw error;
        }
    };
}

function installJQueryMutationHeaders() {
    if (!window.jQuery?.ajaxPrefilter) return;
    window.jQuery.ajaxPrefilter((options, _original, xhr) => {
        const method = String(options.type || options.method || 'GET').toUpperCase();
        if (!MUTATION_METHODS.has(method)) return;
        const topics = mutationTopics(options.url);
        if (!topics.length) return;
        xhr.setRequestHeader('X-UnionStack-Client-Instance', clientInstanceId);
        topics.forEach(beginMutation);
        const complete = options.complete;
        options.complete = function (request, status) {
            topics.forEach(topic => endMutation(topic, request.status >= 200 && request.status < 400));
            if (typeof complete === 'function') complete.apply(this, arguments);
        };
    });
}

installFetchMutationHeaders();
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', installJQueryMutationHeaders, { once: true });
} else {
    installJQueryMutationHeaders();
}

const api = Object.freeze({
    clientInstanceId,
    subscribe,
    invoke,
    start,
    isOwnCompletedMutation,
    get state() { return connection?.state || signalR.HubConnectionState.Disconnected; },
});
window.UnionStackRealtime = api;

export { api as realtime, subscribe, invoke, start };
